import { absolutePosition } from "./auto-fuse";
import { isFeedCable } from "./busbar-conversion";
import { GEOMETRY } from "./canvas-geometry";
import { getDefinition } from "./catalog";
import { effectiveDimensions, effectiveTerminals } from "./effective";
import { isJunction } from "./node-kind";
import type {
  Cable,
  Category,
  ComponentDefinition,
  ComponentNode,
  SystemNode,
  Terminal,
  TerminalDirection,
  TerminalPolarity,
  TerminalSide,
} from "./types";

const { JUNCTION_HALF, MIN_TERMINAL_GAP_PX } = GEOMETRY;

type Pt = { x: number; y: number };

export function terminalPolarity(
  t: Terminal,
): "positive" | "negative" | "other" {
  if (t.polarity === "positive") return "positive";
  if (t.polarity === "negative" || t.polarity === "ground") return "negative";
  return "other";
}

/** First polarity resolved by walking the junction's cables on through other
 *  junctions; "other" when nothing resolves. */
export function inferJunctionPolarity(
  junctionId: string,
  nodes: SystemNode[],
  cables: Cable[],
): "positive" | "negative" | "other" {
  return walkJunction(junctionId, nodes, cables, new Set());
}

function walkJunction(
  junctionId: string,
  nodes: SystemNode[],
  cables: Cable[],
  visited: Set<string>,
): "positive" | "negative" | "other" {
  visited.add(junctionId);
  for (const c of cables) {
    let otherId: string | null = null;
    let otherTermId: string | null = null;
    if (c.fromNodeId === junctionId) {
      otherId = c.toNodeId;
      otherTermId = c.toTerminalId;
    } else if (c.toNodeId === junctionId) {
      otherId = c.fromNodeId;
      otherTermId = c.fromTerminalId;
    }
    if (!otherId || !otherTermId) continue;
    const other = nodes.find((n) => n.id === otherId);
    if (!other) continue;
    if (other.kind === "component") {
      const def = getDefinition(other.definitionId, other.customDefinition);
      if (!def) continue;
      if (def.renderVariant === "junction" && !visited.has(other.id)) {
        const pol = walkJunction(other.id, nodes, cables, visited);
        if (pol !== "other") return pol;
        continue;
      }
      const t = effectiveTerminals(def, other).find((x) => x.id === otherTermId);
      if (!t) continue;
      const pol = terminalPolarity(t);
      if (pol !== "other") return pol;
    }
  }
  return "other";
}

/** Every polarity reachable through the chain of junctions. */
export function junctionConnectedPolarities(
  junctionId: string,
  nodes: SystemNode[],
  cables: Cable[],
): { positive: boolean; negative: boolean } {
  const acc = { positive: false, negative: false };
  collectJunctionPolarities(junctionId, nodes, cables, new Set(), acc);
  return acc;
}

function collectJunctionPolarities(
  junctionId: string,
  nodes: SystemNode[],
  cables: Cable[],
  visited: Set<string>,
  acc: { positive: boolean; negative: boolean },
): void {
  visited.add(junctionId);
  for (const c of cables) {
    let otherId: string | null = null;
    let otherTermId: string | null = null;
    if (c.fromNodeId === junctionId) {
      otherId = c.toNodeId;
      otherTermId = c.toTerminalId;
    } else if (c.toNodeId === junctionId) {
      otherId = c.fromNodeId;
      otherTermId = c.fromTerminalId;
    }
    if (!otherId || !otherTermId) continue;
    const other = nodes.find((n) => n.id === otherId);
    if (!other || other.kind !== "component") continue;
    const def = getDefinition(other.definitionId, other.customDefinition);
    if (!def) continue;
    if (def.renderVariant === "junction") {
      if (!visited.has(other.id)) {
        collectJunctionPolarities(other.id, nodes, cables, visited, acc);
      }
      continue;
    }
    const t = effectiveTerminals(def, other).find((x) => x.id === otherTermId);
    if (!t) continue;
    const pol = terminalPolarity(t);
    if (pol === "positive") acc.positive = true;
    else if (pol === "negative") acc.negative = true;
  }
}

export function terminalAbsPosition(
  node: ComponentNode,
  def: ComponentDefinition,
  terminal: Terminal,
  nodes: SystemNode[],
): Pt {
  const abs = absolutePosition(node, nodes);
  const dims = effectiveDimensions(def, node);
  switch (terminal.side) {
    case "left":
      return { x: abs.x, y: abs.y + dims.height * terminal.offset };
    case "right":
      return { x: abs.x + dims.width, y: abs.y + dims.height * terminal.offset };
    case "top":
      return { x: abs.x + dims.width * terminal.offset, y: abs.y };
    case "bottom":
      return { x: abs.x + dims.width * terminal.offset, y: abs.y + dims.height };
  }
}

type SourceRole = "supply" | "sink" | "passive";

/** Batteries are `passive` on purpose: biasing them toward inputs would send
 *  every leisure battery to a charger's IN+. Vacancy and geometry decide. */
function sourceRole(category: Category, term: Terminal): SourceRole {
  const pol = terminalPolarity(term);
  if (pol === "negative") return "passive";
  switch (category) {
    case "battery":
      return "passive";
    case "solar":
    case "source":
      return "supply";
    case "engine":
      return pol === "positive" ? "supply" : "passive";
    case "charger": {
      if (term.direction === "output") return "supply";
      if (term.direction === "input") return "sink";
      const id = term.id.toLowerCase();
      if (id.startsWith("out-") || id.startsWith("batt-")) return "supply";
      return "sink";
    }
    case "load":
      return "sink";
    case "distribution":
    case "monitor":
      return "passive";
  }
}

/** Source flavour, so a solar feed lands on a charger's `pv-pos`, not `alt-pos`. */
function channelHint(category: Category, terminalId: string): string | null {
  const id = terminalId.toLowerCase();
  if (category === "solar" || id.includes("pv")) return "pv";
  if (category === "engine" || id.includes("alt")) return "alt";
  if (id.includes("start")) return "start";
  return null;
}

/** Terminal on `target` best matching the source. Polarity mismatch is heavily
 *  penalised but never absolute — the snap always resolves rather than refuse. */
export function pickSnapTerminal(args: {
  sourceCategory?: Category;
  sourceTerminalPolarity?: TerminalPolarity;
  sourceTerminalDirection?: TerminalDirection;
  sourceTerminalId?: string;
  sourcePolarity: "positive" | "negative" | "other";
  sourcePoint: Pt;
  /** Pointer position for a body drop; electrical compatibility still wins. */
  targetPoint?: Pt;
  target: ComponentNode;
  targetDef: ComponentDefinition;
  nodes: SystemNode[];
  cables?: Cable[];
}): Terminal | undefined {
  const {
    sourceCategory,
    sourceTerminalPolarity,
    sourceTerminalDirection,
    sourceTerminalId,
    sourcePolarity,
    sourcePoint,
    targetPoint,
    target,
    targetDef,
    nodes,
    cables,
  } = args;
  const all = effectiveTerminals(targetDef, target);
  if (all.length === 0) return undefined;

  const sourceTerm: Terminal | null =
    sourceCategory && sourceTerminalPolarity && sourceTerminalDirection && sourceTerminalId
      ? {
          id: sourceTerminalId,
          label: "",
          polarity: sourceTerminalPolarity,
          direction: sourceTerminalDirection,
          side: "left",
          offset: 0,
          maxAmp: 0,
        }
      : null;
  let role: SourceRole | null =
    sourceCategory && sourceTerm ? sourceRole(sourceCategory, sourceTerm) : null;
  // A battery into a fuse block is always the feed, not a stud competing on geometry.
  if (
    targetDef.isFuseBlock &&
    role === "passive" &&
    sourceCategory === "battery" &&
    sourcePolarity === "positive"
  ) {
    role = "supply";
  }
  const hint =
    sourceCategory && sourceTerminalId ? channelHint(sourceCategory, sourceTerminalId) : null;
  const wiredTerminalIds = new Set<string>();
  if (cables) {
    for (const c of cables) {
      if (c.fromNodeId === target.id) wiredTerminalIds.add(c.fromTerminalId);
      if (c.toNodeId === target.id) wiredTerminalIds.add(c.toTerminalId);
    }
  }

  // A load dropped on a fuse block means "add another fused branch".
  let candidates = all;
  if (
    targetDef.isFuseBlock &&
    role === "sink" &&
    sourcePolarity === "positive" &&
    cables
  ) {
    const freeOutputs = all.filter(
      (t) =>
        terminalPolarity(t) === "positive" &&
        t.direction === "output" &&
        !wiredTerminalIds.has(t.id),
    );
    if (freeOutputs.length > 0) candidates = freeOutputs;
  }
  // Bus bar studs are all electrically common, so the rule is who's feeding: a
  // feed shares the input face, everything else takes a free stud.
  if (
    targetDef.renderVariant === "busbar" &&
    sourcePolarity !== "other" &&
    cables
  ) {
    const studAxis: "left" | "right" | "top" | "bottom" =
      targetDef.height >= targetDef.width ? "right" : "bottom";
    if (isFeedCable(sourceCategory, sourceTerminalDirection)) {
      const feedFace = all.filter(
        (t) => terminalPolarity(t) === sourcePolarity && t.side !== studAxis,
      );
      if (feedFace.length > 0) candidates = feedFace;
    } else {
      const freeStuds = all.filter(
        (t) =>
          terminalPolarity(t) === sourcePolarity &&
          t.side === studAxis &&
          !wiredTerminalIds.has(t.id),
      );
      if (freeStuds.length > 0) candidates = freeStuds;
    }
  }

  let best: Terminal | undefined;
  let bestScore = -Infinity;
  for (const t of candidates) {
    let score = 0;

    if (sourcePolarity !== "other") {
      const tp = terminalPolarity(t);
      if (tp === sourcePolarity) score += 1000;
      else if (tp !== "other") score -= 1000;
    }

    if (role === "supply" && t.direction === "input") score += 200;
    else if (role === "sink" && t.direction === "output") score += 200;
    else if (role === "supply" && t.direction === "output") score -= 200;
    else if (role === "sink" && t.direction === "input") score -= 200;

    // Vacancy already shaped the fuse-block/busbar candidates; scoring it twice skews it.
    const skipVacancy = targetDef.isFuseBlock || targetDef.renderVariant === "busbar";
    if (cables && !skipVacancy && !wiredTerminalIds.has(t.id)) {
      score += 250;
    }

    if (hint && t.id.toLowerCase().includes(hint)) score += 100;

    const pos = terminalAbsPosition(target, targetDef, t, nodes);
    const aim = targetPoint ?? sourcePoint;
    const dx = pos.x - aim.x;
    const dy = pos.y - aim.y;
    const d2 = dx * dx + dy * dy;
    // px² is huge; scale so distance only breaks ties inside a scoring bucket.
    score -= d2 / 10000;

    if (score > bestScore) {
      bestScore = score;
      best = t;
    }
  }
  return best;
}

/** Drag-placement for a busbar stud. The side is preserved — the rod is
 *  physical, so studs slide along it and never jump faces. */
export function busbarRodPlacement(
  node: ComponentNode,
  def: ComponentDefinition,
  nodes: SystemNode[],
  cursor: Pt,
  terminal: Terminal,
): { side: TerminalSide; offset: number } {
  const abs = absolutePosition(node, nodes);
  const visualDims = effectiveDimensions(def, node);
  const cx = abs.x + visualDims.width / 2;
  const cy = abs.y + visualDims.height / 2;
  const dx = cursor.x - cx;
  const dy = cursor.y - cy;
  const r = node.rotation ?? 0;
  let lx: number;
  let ly: number;
  if (r === 90) { lx = dy; ly = -dx; }
  else if (r === 180) { lx = -dx; ly = -dy; }
  else if (r === 270) { lx = -dy; ly = dx; }
  else { lx = dx; ly = dy; }
  const catX = lx + def.width / 2;
  const catY = ly + def.height / 2;
  const isVerticalRod = terminal.side === "left" || terminal.side === "right";
  const raw = isVerticalRod ? catY / def.height : catX / def.width;
  // Keep clear of sibling studs, or their rendered fuse pills overlap.
  const sideLengthPx = isVerticalRod ? visualDims.height : visualDims.width;
  const minGap = MIN_TERMINAL_GAP_PX / Math.max(1, sideLengthPx);
  const others = effectiveTerminals(def, { ...node, rotation: 0 })
    .filter((t) => t.side === terminal.side && t.id !== terminal.id)
    .map((t) => t.offset);
  const offset = avoidStudCollisions(raw, others, minGap, 0.1, 0.9);
  return { side: terminal.side, offset };
}

function avoidStudCollisions(
  raw: number,
  others: number[],
  minGap: number,
  lo: number,
  hi: number,
): number {
  let result = clamp(raw, lo, hi);
  if (others.length === 0) return result;
  for (let iter = 0; iter < others.length + 2; iter++) {
    let moved = false;
    for (const o of others) {
      if (Math.abs(result - o) < minGap) {
        const candidate = raw <= o ? o - minGap : o + minGap;
        result = clamp(candidate, lo, hi);
        moved = true;
      }
    }
    if (!moved) break;
  }
  return result;
}

/** Side+offset on `target` nearest `sourcePoint` (absolute canvas coords). */
export function nearestSidePlacement(
  target: ComponentNode,
  targetDef: ComponentDefinition,
  nodes: SystemNode[],
  sourcePoint: Pt,
): { side: TerminalSide; offset: number } {
  const abs = absolutePosition(target, nodes);
  const dims = effectiveDimensions(targetDef, target);
  const cx = abs.x + dims.width / 2;
  const cy = abs.y + dims.height / 2;
  const dx = sourcePoint.x - cx;
  const dy = sourcePoint.y - cy;

  // Compare against half-dimensions so wide nodes prefer top/bottom less.
  const horiz = Math.abs(dx) / Math.max(1, dims.width / 2);
  const vert = Math.abs(dy) / Math.max(1, dims.height / 2);

  if (horiz >= vert) {
    const side: TerminalSide = dx >= 0 ? "right" : "left";
    const offset = clamp((sourcePoint.y - abs.y) / dims.height, 0.1, 0.9);
    return { side, offset };
  }
  const side: TerminalSide = dy >= 0 ? "bottom" : "top";
  const offset = clamp((sourcePoint.x - abs.x) / dims.width, 0.1, 0.9);
  return { side, offset };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Absolute point of the cable end opposite the terminal we're placing. */
function otherEndPoint(
  otherNode: SystemNode,
  otherTerminalId: string,
  nodes: SystemNode[],
): Pt | null {
  if (otherNode.kind === "zone") return null;
  const def = getDefinition(otherNode.definitionId, otherNode.customDefinition);
  if (def?.renderVariant === "junction") {
    const abs = absolutePosition(otherNode, nodes);
    return { x: abs.x + JUNCTION_HALF, y: abs.y + JUNCTION_HALF };
  }
  if (!def) return null;
  const t = effectiveTerminals(def, otherNode).find((x) => x.id === otherTerminalId);
  if (!t) return null;
  return terminalAbsPosition(otherNode, def, t, nodes);
}

/** Walk any junction chain to the first real component, so terminal ordering
 *  has a position to sort by. */
function resolveFarComponentPoint(
  startNodeId: string,
  startTerminalId: string,
  fromNodeId: string,
  nodes: SystemNode[],
  cables: Cable[],
): Pt | null {
  const startNode = nodes.find((n) => n.id === startNodeId);
  if (!startNode) return null;
  if (!isJunction(startNode)) {
    return otherEndPoint(startNode, startTerminalId, nodes);
  }
  const visited = new Set<string>([fromNodeId, startNodeId]);
  const queue: string[] = [startNodeId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    for (const c of cables) {
      let nextId: string | null = null;
      let nextTerm: string | null = null;
      if (c.fromNodeId === id) {
        nextId = c.toNodeId;
        nextTerm = c.toTerminalId;
      } else if (c.toNodeId === id) {
        nextId = c.fromNodeId;
        nextTerm = c.fromTerminalId;
      }
      if (!nextId || !nextTerm || visited.has(nextId)) continue;
      visited.add(nextId);
      const nextNode = nodes.find((n) => n.id === nextId);
      if (!nextNode) continue;
      if (isJunction(nextNode)) {
        queue.push(nextId);
      } else if (nextNode.kind === "component") {
        return otherEndPoint(nextNode, nextTerm, nodes);
      }
    }
  }
  return otherEndPoint(startNode, startTerminalId, nodes);
}

/**
 * Terminal placements facing each cable's far end, min-gap kept per side. Only
 * connected terminals are written back. `newAbsPos` is the dragged node's
 * absolute top-left — the in-flight drag frame, not its stored position.
 */
export function optimalTerminalOverrides(args: {
  node: ComponentNode;
  def: ComponentDefinition;
  nodes: SystemNode[];
  cables: Cable[];
  newAbsPos: Pt;
}): Record<string, { side: TerminalSide; offset: number }> {
  const { node, def, nodes, cables, newAbsPos } = args;
  // Busbar studs are physical points on the rod, and `effectiveTerminals`
  // ignores overrides on them anyway.
  if (def.renderVariant === "busbar") return {};
  // Symbol glyphs draw leads at fixed points; a re-aimed handle floats off the lead end.
  if (
    def.renderVariant === "switch" ||
    def.renderVariant === "fuse-holder" ||
    def.renderVariant === "chassis-ground"
  )
    return {};
  const dims = effectiveDimensions(def, node);
  const cx = newAbsPos.x + dims.width / 2;
  const cy = newAbsPos.y + dims.height / 2;

  const terms = effectiveTerminals(def, node);
  const overridesIn = node.terminalOverrides ?? {};

  const desired: Desired[] = [];
  for (const t of terms) {
    // A pinned placement keeps its offset but still joins the side's
    // distribution, so auto-aimed terminals leave the min-gap around it.
    const pinnedOv = overridesIn[t.id];
    if (pinnedOv?.pinned) {
      const pinnedSide = pinnedOv.side ?? t.side;
      const pinnedOffset = pinnedOv.offset ?? t.offset;
      desired.push({
        id: t.id,
        side: pinnedSide,
        rawOffset: pinnedOffset,
        farX: cx,
        farY: cy,
        pinnedOffset,
      });
      continue;
    }
    // Approach (where the cable enters) picks the side; the far end picks the
    // offset. Waypoints can send the two different ways.
    let approachSumX = 0;
    let approachSumY = 0;
    let approachCount = 0;
    let farSumX = 0;
    let farSumY = 0;
    let farCount = 0;
    for (const c of cables) {
      let isFromSide = false;
      let otherNodeId: string | null = null;
      let otherTerminalId: string | null = null;
      if (c.fromNodeId === node.id && c.fromTerminalId === t.id) {
        isFromSide = true;
        otherNodeId = c.toNodeId;
        otherTerminalId = c.toTerminalId;
      } else if (c.toNodeId === node.id && c.toTerminalId === t.id) {
        otherNodeId = c.fromNodeId;
        otherTerminalId = c.fromTerminalId;
      }
      if (!otherNodeId || !otherTerminalId) continue;
      const farPt = resolveFarComponentPoint(
        otherNodeId,
        otherTerminalId,
        node.id,
        nodes,
        cables,
      );
      const wps = c.waypoints;
      const approachPt: Pt | null =
        wps && wps.length > 0
          ? isFromSide
            ? wps[0]
            : wps[wps.length - 1]
          : farPt;
      if (approachPt) {
        approachSumX += approachPt.x;
        approachSumY += approachPt.y;
        approachCount += 1;
      }
      if (farPt) {
        farSumX += farPt.x;
        farSumY += farPt.y;
        farCount += 1;
      }
    }
    if (approachCount === 0) continue;
    const approachX = approachSumX / approachCount;
    const approachY = approachSumY / approachCount;
    const farX = farCount > 0 ? farSumX / farCount : approachX;
    const farY = farCount > 0 ? farSumY / farCount : approachY;
    const dx = approachX - cx;
    const dy = approachY - cy;
    const horiz = Math.abs(dx) / Math.max(1, dims.width / 2);
    const vert = Math.abs(dy) / Math.max(1, dims.height / 2);
    if (horiz >= vert) {
      const side: TerminalSide = dx >= 0 ? "right" : "left";
      desired.push({
        id: t.id,
        side,
        rawOffset: (farY - newAbsPos.y) / dims.height,
        farX,
        farY,
      });
    } else {
      const side: TerminalSide = dy >= 0 ? "bottom" : "top";
      desired.push({
        id: t.id,
        side,
        rawOffset: (farX - newAbsPos.x) / dims.width,
        farX,
        farY,
      });
    }
  }

  if (desired.length === 0) return {};

  const bySide = new Map<TerminalSide, Desired[]>();
  for (const d of desired) {
    const list = bySide.get(d.side) ?? [];
    list.push(d);
    bySide.set(d.side, list);
  }
  spillOverflow(bySide, dims);

  const overrides: Record<string, { side: TerminalSide; offset: number }> = {};
  for (const [side, group] of bySide) {
    const sideLength = side === "left" || side === "right" ? dims.height : dims.width;
    const placements = distributeOnSide(group, sideLength);
    // rawOffset ties when two far ends share an axis, so swap any crossing pair.
    uncrossOnSide(group, placements, side, newAbsPos, dims);
    for (const entry of group) {
      if (entry.pinnedOffset != null) continue;
      const offset = placements.get(entry.id);
      if (offset == null) continue;
      overrides[entry.id] = { side, offset };
    }
  }
  return overrides;
}

/** Swap auto-aimed offsets until the side's cables stop crossing; pinned ones never move. */
function uncrossOnSide(
  group: Desired[],
  placements: Map<string, number>,
  side: TerminalSide,
  newAbsPos: Pt,
  dims: { width: number; height: number },
): void {
  const auto = group.filter((g) => g.pinnedOffset == null);
  if (auto.length < 2) return;
  for (let pass = 0; pass < auto.length; pass++) {
    let swapped = false;
    for (let i = 0; i < auto.length - 1; i++) {
      for (let j = i + 1; j < auto.length; j++) {
        const a = auto[i];
        const b = auto[j];
        const oa = placements.get(a.id);
        const ob = placements.get(b.id);
        if (oa == null || ob == null) continue;
        const crossesNow = pathsCross(a, oa, b, ob, side, newAbsPos, dims);
        const crossesSwapped = pathsCross(a, ob, b, oa, side, newAbsPos, dims);
        if (crossesNow && !crossesSwapped) {
          placements.set(a.id, ob);
          placements.set(b.id, oa);
          swapped = true;
        }
      }
    }
    if (!swapped) return;
  }
}

function pathsCross(
  a: Desired,
  offsetA: number,
  b: Desired,
  offsetB: number,
  side: TerminalSide,
  newAbsPos: Pt,
  dims: { width: number; height: number },
): boolean {
  const pathA = simpleTwoSegPath(offsetA, a.farX, a.farY, side, newAbsPos, dims);
  const pathB = simpleTwoSegPath(offsetB, b.farX, b.farY, side, newAbsPos, dims);
  for (let i = 0; i < pathA.length - 1; i++) {
    for (let j = 0; j < pathB.length - 1; j++) {
      if (orthogonalSegmentsCross(pathA[i], pathA[i + 1], pathB[j], pathB[j + 1])) {
        return true;
      }
    }
  }
  return false;
}

function simpleTwoSegPath(
  offset: number,
  farX: number,
  farY: number,
  side: TerminalSide,
  newAbsPos: Pt,
  dims: { width: number; height: number },
): Pt[] {
  // One bend on the stub axis, as the tidy router draws it, so the check judges
  // the path the user sees.
  let term: Pt;
  let bend: Pt;
  switch (side) {
    case "left":
    case "right":
      term = {
        x: side === "left" ? newAbsPos.x : newAbsPos.x + dims.width,
        y: newAbsPos.y + offset * dims.height,
      };
      bend = { x: farX, y: term.y };
      break;
    case "top":
    case "bottom":
      term = {
        x: newAbsPos.x + offset * dims.width,
        y: side === "top" ? newAbsPos.y : newAbsPos.y + dims.height,
      };
      bend = { x: term.x, y: farY };
      break;
  }
  return [term, bend, { x: farX, y: farY }];
}

function orthogonalSegmentsCross(a1: Pt, a2: Pt, b1: Pt, b2: Pt): boolean {
  const aHoriz = a1.y === a2.y;
  const bHoriz = b1.y === b2.y;
  if (aHoriz === bHoriz) return false;
  const horiz = aHoriz ? { p1: a1, p2: a2 } : { p1: b1, p2: b2 };
  const vert = aHoriz ? { p1: b1, p2: b2 } : { p1: a1, p2: a2 };
  const hy = horiz.p1.y;
  const vx = vert.p1.x;
  const hx0 = Math.min(horiz.p1.x, horiz.p2.x);
  const hx1 = Math.max(horiz.p1.x, horiz.p2.x);
  const vy0 = Math.min(vert.p1.y, vert.p2.y);
  const vy1 = Math.max(vert.p1.y, vert.p2.y);
  return vx > hx0 && vx < hx1 && hy > vy0 && hy < vy1;
}

const { SIDE_MARGIN } = GEOMETRY;

/** How many terminals fit on a side, keeping margin and min-gap. Always ≥ 1. */
function maxFitForSide(sideLengthPx: number): number {
  const inner = 1 - 2 * SIDE_MARGIN;
  const minGap = MIN_TERMINAL_GAP_PX / Math.max(1, sideLengthPx);
  return Math.max(1, Math.floor(inner / minGap) + 1);
}

/** Space terminals inside the windows pinned ones leave. Un-pinned only. */
function distributeOnSide(
  group: Desired[],
  sideLengthPx: number,
): Map<string, number> {
  const out = new Map<string, number>();
  const minGap = MIN_TERMINAL_GAP_PX / Math.max(1, sideLengthPx);
  const lo = SIDE_MARGIN;
  const hi = 1 - SIDE_MARGIN;
  const pinnedOffsets = group
    .filter((g) => g.pinnedOffset != null)
    .map((g) => g.pinnedOffset as number)
    .sort((a, b) => a - b);
  const autoEntries = group.filter((g) => g.pinnedOffset == null);
  if (autoEntries.length === 0) return out;

  type Window = { innerStart: number; innerEnd: number; entries: Desired[] };
  const windows: Window[] = [];
  if (pinnedOffsets.length === 0) {
    windows.push({ innerStart: lo, innerEnd: hi, entries: [] });
  } else {
    windows.push({
      innerStart: lo,
      innerEnd: pinnedOffsets[0] - minGap,
      entries: [],
    });
    for (let i = 0; i < pinnedOffsets.length - 1; i++) {
      windows.push({
        innerStart: pinnedOffsets[i] + minGap,
        innerEnd: pinnedOffsets[i + 1] - minGap,
        entries: [],
      });
    }
    windows.push({
      innerStart: pinnedOffsets[pinnedOffsets.length - 1] + minGap,
      innerEnd: hi,
      entries: [],
    });
  }

  for (const entry of autoEntries) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < windows.length; i++) {
      const w = windows[i];
      const inWindow =
        entry.rawOffset >= w.innerStart && entry.rawOffset <= w.innerEnd;
      const d = inWindow
        ? 0
        : Math.min(
            Math.abs(entry.rawOffset - w.innerStart),
            Math.abs(entry.rawOffset - w.innerEnd),
          );
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    windows[bestIdx].entries.push(entry);
  }

  for (const w of windows) {
    if (w.entries.length === 0) continue;
    w.entries.sort((a, b) => a.rawOffset - b.rawOffset);
    const innerLen = Math.max(0, w.innerEnd - w.innerStart);
    const n = w.entries.length;

    // Honour rawOffsets when the whole set clears min-gap, so cables run
    // straight; otherwise they disambiguate nothing and even spacing wins.
    const aligned: number[] = [];
    let alignmentOk = true;
    for (let i = 0; i < n; i++) {
      const target = w.entries[i].rawOffset;
      const lower = i === 0 ? w.innerStart : aligned[i - 1] + minGap;
      const upper =
        i === n - 1
          ? w.innerEnd
          : Math.min(w.innerEnd, w.entries[i + 1].rawOffset - minGap);
      if (lower > upper) {
        alignmentOk = false;
        break;
      }
      aligned.push(Math.max(lower, Math.min(upper, target)));
    }
    if (alignmentOk && aligned.length === n) {
      for (let i = 0; i < n; i++) out.set(w.entries[i].id, aligned[i]);
      continue;
    }

    const evenGap = n > 1 ? innerLen / (n - 1) : 0;
    const gap = Math.max(evenGap, minGap);
    const span = (n - 1) * gap;
    const start = w.innerStart + (innerLen - span) / 2;
    for (let i = 0; i < n; i++) {
      out.set(w.entries[i].id, start + i * gap);
    }
  }

  return out;
}

const PERPENDICULAR: Record<TerminalSide, [TerminalSide, TerminalSide]> = {
  left: ["top", "bottom"],
  right: ["top", "bottom"],
  top: ["left", "right"],
  bottom: ["left", "right"],
};

type Desired = {
  id: string;
  side: TerminalSide;
  rawOffset: number;
  /** Far-end coords: tells two terminals apart when they share a `rawOffset`. */
  farX: number;
  farY: number;
  /** Set when user-pinned: the terminal must keep this offset. */
  pinnedOffset?: number;
};

/** Move terminals off an over-full side onto the roomiest perpendicular side. */
function spillOverflow(
  bySide: Map<TerminalSide, Desired[]>,
  dims: { width: number; height: number },
): void {
  const lengthOf = (s: TerminalSide) =>
    s === "left" || s === "right" ? dims.height : dims.width;

  let safety = 16;
  while (safety-- > 0) {
    let movedAny = false;
    for (const [side, group] of bySide) {
      const cap = maxFitForSide(lengthOf(side));
      if (group.length <= cap) continue;
      // Nearest a corner is the least-anchored; pinned terminals never move sides.
      let pickIdx = -1;
      let bestScore = Infinity;
      for (let i = 0; i < group.length; i++) {
        if (group[i].pinnedOffset != null) continue;
        const r = group[i].rawOffset;
        const score = Math.min(r, 1 - r);
        if (score < bestScore) {
          bestScore = score;
          pickIdx = i;
        }
      }
      if (pickIdx < 0) continue;
      const moved = group[pickIdx];
      group.splice(pickIdx, 1);
      const [a, b] = PERPENDICULAR[side];
      const capA = maxFitForSide(lengthOf(a)) - (bySide.get(a)?.length ?? 0);
      const capB = maxFitForSide(lengthOf(b)) - (bySide.get(b)?.length ?? 0);
      let target: TerminalSide;
      if (capA > capB) target = a;
      else if (capB > capA) target = b;
      else target = moved.rawOffset < 0.5 ? a : b;
      const list = bySide.get(target) ?? [];
      list.push({
        id: moved.id,
        side: target,
        rawOffset: 0.5,
        farX: moved.farX,
        farY: moved.farY,
      });
      bySide.set(target, list);
      movedAny = true;
      break;
    }
    if (!movedAny) return;
  }
}
